import { CartAddEvent } from '@theme/events';

/**
 * <cart-upsell> — In-cart upsell ("Frequently purchased with").
 *
 * Picks, in order of preference:
 *   1. CURATED companions — the seed product's "Often bought together" list
 *      (custom.bundle_products), server-rendered into data-curated. Same source
 *      as the product-page bundle, so the cart upsell shows hand-picked items.
 *   2. Shopify related-product recommendations (data-fallback="related", the
 *      default). Set data-fallback="none" to show nothing when there's no
 *      curated list instead of falling back.
 *
 * In both cases items already in the cart and out-of-stock products are filtered
 * out, and up to data-limit cards are rendered.
 *
 * Horizon morphs the cart section (drawer hydration + every cart:update), which
 * can wipe JS-injected content. To stay resilient we cache the recommendations
 * and re-render whenever our cards get removed (MutationObserver) or the cart
 * changes (cart:update), plus a one-shot retry safety net. data-curated is an
 * attribute (survives our own innerHTML render) and is refreshed by the morph.
 *
 * Adding a single-variant product is done natively: POST /cart/add.js with the
 * cart section ids, then dispatch CartAddEvent so cart-items-component morphs
 * the cart and cart-drawer-component opens. Multi-variant products link to the
 * product page so the customer can choose options.
 */

/** @type {Map<string, Promise<any[]>>} seed product id -> recommended products */
const recommendationCache = new Map();

class CartUpsell extends HTMLElement {
  /** @type {any[] | null} */
  #picks = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retry;

  #mutationObserver = new MutationObserver(() => {
    // A morph wiped our cards but we have recommendations — re-render them.
    if (this.#picks && this.#picks.length && !this.querySelector('[data-upsell-card]')) {
      this.#renderCards();
    }
  });

  connectedCallback() {
    this.#mutationObserver.observe(this, { childList: true });
    document.addEventListener('cart:update', this.#onCartUpdate);
    this.#run();

    // Safety net: if anything raced (e.g. a morph during the initial fetch) and
    // we're still showing the skeleton a few seconds later, try once more.
    this.#retry = setTimeout(() => {
      if (this.hasAttribute('data-loading') && !this.hidden) this.#run();
    }, 3000);
  }

  disconnectedCallback() {
    this.#mutationObserver.disconnect();
    document.removeEventListener('cart:update', this.#onCartUpdate);
    clearTimeout(this.#debounce);
    clearTimeout(this.#retry);
  }

  #onCartUpdate = () => {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#run(), 80);
  };

  get #limit() {
    return parseInt(this.dataset.limit || '2', 10);
  }

  get #excluded() {
    return new Set((this.dataset.cartIds || '').split(',').filter(Boolean));
  }

  /** Curated companions from the seed product's metafield (server-rendered). */
  #curatedPicks() {
    let list;
    try {
      list = JSON.parse(this.dataset.curated || '[]');
    } catch {
      return [];
    }
    if (!Array.isArray(list) || !list.length) return [];
    const excluded = this.#excluded;
    return list
      .filter((product) => product.available && !excluded.has(String(product.id)))
      .slice(0, this.#limit);
  }

  async #run() {
    const seed = this.dataset.productId;
    if (!seed) return this.#hide();

    // 1) Prefer the seed product's curated "Often bought together" companions.
    const curated = this.#curatedPicks();
    if (curated.length) {
      this.#picks = curated;
      this.#renderCards();
      return;
    }

    // 2) Fall back to Shopify's related recommendations (unless disabled).
    if (this.dataset.fallback === 'none') return this.#hide();

    try {
      const base = this.dataset.url || '/recommendations/products';
      if (!recommendationCache.has(seed)) {
        recommendationCache.set(
          seed,
          fetch(`${base}.json?product_id=${encodeURIComponent(seed)}&intent=related&limit=10`)
            .then((response) =>
              response.ok ? response.json() : Promise.reject(new Error(`recommendations ${response.status}`))
            )
            .then((data) => data.products || [])
        );
      }

      const products = await recommendationCache.get(seed);
      const excluded = this.#excluded;
      this.#picks = products
        .filter((product) => product.available && !excluded.has(String(product.id)))
        .slice(0, this.#limit);

      if (!this.#picks.length) return this.#hide();
      this.#renderCards();
    } catch (error) {
      console.error('[cart-upsell]', error);
      this.#hide();
    }
  }

  #hide() {
    this.hidden = true;
  }

  #renderCards() {
    if (!this.#picks || !this.#picks.length) return;

    // Pause the observer while we mutate our own content so it doesn't re-fire.
    this.#mutationObserver.disconnect();

    this.hidden = false;
    this.removeAttribute('data-loading');

    const heading = this.dataset.heading || 'Frequently purchased with';
    this.innerHTML =
      `<p class="cart-upsell__heading">${this.#escape(heading)}</p>` +
      `<div class="cart-upsell__list">${this.#picks.map((product) => this.#cardMarkup(product)).join('')}</div>`;

    this.querySelectorAll('[data-upsell-add]').forEach((button) => {
      button.addEventListener('click', this.#onAdd);
    });

    this.#mutationObserver.observe(this, { childList: true });
  }

  #cardMarkup(product) {
    const variants = product.variants || [];
    const sellable = variants.length === 1 && variants[0]?.available ? variants[0] : null;
    const image = product.featured_image || (product.images && product.images[0]) || '';
    const imageUrl = image ? `${image}${image.includes('?') ? '&' : '?'}width=240` : '';

    const action = sellable
      ? `<button type="button" class="cart-upsell__cta" data-upsell-add data-variant-id="${sellable.id}">
           <span class="cart-upsell__cta-text">${this.#escape(this.dataset.addLabel || 'Add to cart')}</span>
         </button>`
      : `<a class="cart-upsell__cta" href="${this.#escape(product.url)}">
           <span class="cart-upsell__cta-text">${this.#escape(this.dataset.chooseLabel || 'Choose options')}</span>
         </a>`;

    return `
      <div class="cart-upsell__card" data-upsell-card>
        <a class="cart-upsell__media" href="${this.#escape(product.url)}" tabindex="-1" aria-hidden="true">
          ${imageUrl ? `<img src="${this.#escape(imageUrl)}" alt="" width="84" height="84" loading="lazy">` : ''}
        </a>
        <div class="cart-upsell__info">
          <a class="cart-upsell__title" href="${this.#escape(product.url)}">${this.#escape(product.title)}</a>
          <span class="cart-upsell__price">${this.#money(product.price)}</span>
          ${action}
        </div>
      </div>`;
  }

  #onAdd = async (event) => {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    if (!variantId || button.hasAttribute('aria-disabled')) return;

    button.setAttribute('aria-disabled', 'true');
    button.classList.add('cart-upsell__cta--loading');

    try {
      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((node) => node.dataset.sectionId)
        .filter(Boolean);

      const response = await fetch(`${window.Theme?.routes?.cart_add_url || '/cart/add.js'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'add failed');
      }

      button.closest('[data-upsell-card]')?.classList.add('cart-upsell__card--added');

      // Tell the theme: morph the cart sections and open the drawer.
      document.dispatchEvent(
        new CartAddEvent(data, 'cart-upsell', {
          source: 'cart-upsell',
          sections: data.sections,
          variantId: String(variantId),
        })
      );
    } catch (error) {
      console.error('[cart-upsell] add', error);
      button.removeAttribute('aria-disabled');
      button.classList.remove('cart-upsell__cta--loading');
    }
  };

  #money(cents) {
    const amount = (Number(cents) || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }

  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
    );
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}

